import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  calculateCustomerQuoteAllocation,
  confirmCustomerQuoteSourceChange,
  fetchCustomerQuoteExcelDownload,
  exportCustomerQuoteMarkdown,
  fetchCustomerQuoteEditor,
  finalizeCustomerQuote,
  generateCustomerQuoteAutoTarget,
  repriceCustomerQuoteTarget,
  previewCustomerQuote,
  saveCustomerQuoteDraft,
  triggerCustomerQuoteExcelDownload,
} from "../repo";
import { isHttpClientError } from "../../../lib/http/errors";
import {
  applyCustomerQuoteAgentCollection,
  applyCustomerQuoteLineUnitMarkup,
  applyCustomerQuotePricingGroupMarkup,
  applyCustomerQuotePeopleCounts,
  applyCustomerQuoteRoleUnitPrice,
  applyCustomerQuoteTemplate,
  isCustomerQuotePersonNightStay,
  calculateCustomerQuoteRoleSummary,
  calculateCustomerQuoteStandardFinancialSummary,
  calculateCustomerQuoteSummary,
  createManualCustomerQuoteLine,
  customerQuoteFeeCategoryLabels,
  patchCustomerQuoteLine,
  recalculateCustomerQuoteTax,
  roundCustomerQuoteAutoServiceFee,
} from "../service/draftService";
import type {
  CustomerQuoteAllocationResult,
  CustomerQuoteLine,
  CustomerQuoteEditorData,
  CustomerQuotePreview,
  CustomerQuoteSnapshot,
  CustomerQuoteTemplateType,
} from "../types";

const queryKey = (customerQuoteId: string) => ["customerQuoteEditor", customerQuoteId] as const;
const supersededPreviewMessage = "客户报价预览已被新请求替代";
const supersededAllocationMessage = "标准报价试算已被新请求或编辑替代";
const supersededWriteMessage = "报价页面已切换，已忽略旧报价响应";

type StandardAllocationState = {
  snapshotFingerprint: string;
  data: CustomerQuoteAllocationResult;
  financialSummary: ReturnType<typeof calculateCustomerQuoteStandardFinancialSummary>;
};

type StandardAllocationErrorState = {
  snapshotFingerprint: string;
  error: Error;
};

type WriteRequestContext = {
  customerQuoteId: string;
  generation: number;
  requestId: number;
  kind?: "source-confirmation";
};

type DraftCheckpoint = {
  editRevision: number;
  versionName: string;
  remark: string;
  includedItemsText: string;
  excludedItemsText: string;
};

type ConfirmationQueue = {
  request: WriteRequestContext;
  tail: Promise<void>;
  pending: Map<string, Promise<CustomerQuoteEditorData>>;
  blockedError: unknown;
};

type QuoteRequestContext = Pick<WriteRequestContext, "customerQuoteId" | "generation">;

type PreviewRequestContext = QuoteRequestContext & Pick<WriteRequestContext, "requestId">;

type SuccessfulPreview = QuoteRequestContext & {
  snapshotFingerprint: string;
  templateType: CustomerQuoteTemplateType;
};

/** 保留输入原文时同步解析费用条目，避免草稿和预览漏掉尚未失焦的内容。 */
function splitItems(value: string): string[] {
  return value.split(/[\n,，]/).map((item) => item.trim()).filter(Boolean);
}

/** 判断写请求是否属于指定报价页面代次。 */
function belongsToQuoteContext(
  request: QuoteRequestContext | undefined,
  context: QuoteRequestContext,
): boolean {
  return request?.customerQuoteId === context.customerQuoteId
    && request.generation === context.generation;
}

/** 为每个报价页面代次生成独立写锁键。 */
function writeLockKey(context: QuoteRequestContext): string {
  return `${context.generation}:${context.customerQuoteId}`;
}

/** 从请求发起时的编辑器快照构造双修订写命令。 */
function sourceWriteCommand(editor: CustomerQuoteEditorData | null) {
  if (!editor?.sourceRevision) throw new Error("来源核价修订不可用，请刷新后重试");
  return {
    expectedQuoteRevision: editor.quoteRevision,
    expectedSourceRevision: editor.sourceRevision,
  };
}

/** 将数量、金额和比例字符串归一为可比较的十进制语义值，可空来源字段原样保留。 */
function canonicalDecimal(value: string): string;
function canonicalDecimal(value: null): null;
function canonicalDecimal(value: undefined): undefined;
function canonicalDecimal(value: string | null): string | null;
function canonicalDecimal(value: string | undefined): string | undefined;
function canonicalDecimal(value: string | null | undefined): string | null | undefined;
function canonicalDecimal(value: string | null | undefined): string | null | undefined {
  if (value === null || value === undefined) return value;
  const match = /^([+-]?)(\d+)(?:\.(\d*))?$/.exec(value.trim());
  if (!match) return value;
  const integer = (match[2] ?? "0").replace(/^0+(?=\d)/, "");
  const fraction = (match[3] ?? "").replace(/0+$/, "");
  const unsigned = fraction ? `${integer}.${fraction}` : integer;
  if (/^0(?:\.0*)?$/.test(unsigned)) return "0";
  return `${match[1] === "-" ? "-" : ""}${unsigned}`;
}

/** 只归一报价快照中的十进制业务字段，ID 与普通文本仍严格比较。 */
function canonicalSnapshot(snapshot: CustomerQuoteSnapshot): CustomerQuoteSnapshot {
  return {
    ...snapshot,
    sourceTargetMarginRate: canonicalDecimal(snapshot.sourceTargetMarginRate),
    targetMarginRate: canonicalDecimal(snapshot.targetMarginRate),
    lines: snapshot.lines.map((line) => ({
      ...line,
      sourceQuantity: canonicalDecimal(line.sourceQuantity),
      quantity: canonicalDecimal(line.quantity),
      costUnitPrice: canonicalDecimal(line.costUnitPrice),
      sourceCostAmount: canonicalDecimal(line.sourceCostAmount),
      costAmount: canonicalDecimal(line.costAmount),
      quoteUnitPrice: canonicalDecimal(line.quoteUnitPrice),
      quoteAmount: canonicalDecimal(line.quoteAmount),
    })),
  };
}

/** 生成键顺序稳定的 JSON，避免对象构造顺序影响草稿相等判断。 */
function stableJson(value: unknown): string {
  return JSON.stringify(value, (_key, current: unknown) => {
    if (!current || typeof current !== "object" || Array.isArray(current)) return current;
    return Object.keys(current as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((sorted, key) => {
        sorted[key] = (current as Record<string, unknown>)[key];
        return sorted;
      }, {});
  });
}

/** 按实际客户模板比较正式快照；标准试算不改变此输出口径。 */
function previewFingerprint(snapshot: CustomerQuoteSnapshot, templateType = snapshot.templateType): string {
  return stableJson(canonicalSnapshot(applyCustomerQuoteTemplate(snapshot, templateType)));
}

/** 判断当前三段草稿是否与服务端编辑器基线等价。 */
function draftMatchesEditor(
  editor: CustomerQuoteEditorData,
  snapshot: CustomerQuoteSnapshot,
  versionName: string,
  remark: string,
): boolean {
  return versionName === editor.versionName
    && remark === editor.remark
    && stableJson(canonicalSnapshot(snapshot)) === stableJson(canonicalSnapshot(editor.snapshot));
}

/**
 * 对客报价编辑器运行时：维护未保存快照，并统一调度保存、预览、来源确认与定稿。
 * @param customerQuoteId 报价版本 ID
 */
export function useCustomerQuoteRuntime(customerQuoteId: string) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKey(customerQuoteId),
    queryFn: () => fetchCustomerQuoteEditor(customerQuoteId),
    enabled: Boolean(customerQuoteId),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const queriedEditor =
    query.data && !query.isPlaceholderData && query.data.customerQuoteId === customerQuoteId
      ? query.data
      : null;
  const [editorBaseline, setEditorBaseline] = useState<CustomerQuoteEditorData | null>(null);
  const editor = editorBaseline?.customerQuoteId === customerQuoteId ? editorBaseline : null;
  const handledQueryEditorRef = useRef<CustomerQuoteEditorData | null>(null);
  const [snapshot, setSnapshot] = useState<CustomerQuoteSnapshot | null>(null);
  const snapshotRef = useRef<CustomerQuoteSnapshot | null>(null);
  const editorRef = useRef<CustomerQuoteEditorData | null>(null);
  const [versionName, setVersionName] = useState("");
  const versionNameError = editor && !versionName.trim() ? "请填写报价版本名称，不能只输入空格。" : null;
  const [includedItemsText, setIncludedItemsText] = useState("");
  const [excludedItemsText, setExcludedItemsText] = useState("");
  const [remark, setRemark] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [editError, setEditError] = useState<Error | null>(null);
  const [reloadError, setReloadError] = useState<Error | null>(null);
  const [writeConflict, setWriteConflict] = useState<Error | null>(null);
  const [loadingLatestAfterConflict, setLoadingLatestAfterConflict] = useState(false);
  const [conflictReloadError, setConflictReloadError] = useState<Error | null>(null);
  const [previewData, setPreviewData] = useState<CustomerQuotePreview | null>(null);
  const [previewError, setPreviewError] = useState<Error | null>(null);
  const [successfulPreview, setSuccessfulPreview] = useState<SuccessfulPreview | null>(null);
  const successfulPreviewRef = useRef<SuccessfulPreview | null>(null);
  const [standardAllocationState, setStandardAllocationState] =
    useState<StandardAllocationState | null>(null);
  const [standardAllocationErrorState, setStandardAllocationErrorState] =
    useState<StandardAllocationErrorState | null>(null);
  const latestPreviewRequestRef = useRef(0);
  const latestStandardAllocationRequestRef = useRef(0);
  const activeQuoteRef = useRef({ customerQuoteId, generation: 0 });
  const mountedRef = useRef(false);
  const writeRequestSequenceRef = useRef(0);
  const activeWriteRequestsRef = useRef(new Map<string, WriteRequestContext>());
  const confirmationQueuesRef = useRef(new Map<string, ConfirmationQueue>());
  const [confirmationActivity, setConfirmationActivity] = useState<{ context: QuoteRequestContext; count: number } | null>(null);
  const editRevisionRef = useRef(0);
  const confirmationEditsRef = useRef<Array<{ key: string; revision: number; apply: (value: CustomerQuoteSnapshot) => CustomerQuoteSnapshot }>>([]);
  const draftInputRef = useRef({ versionName, remark, includedItemsText, excludedItemsText });
  const latestReloadRequestRef = useRef<WriteRequestContext | null>(null);
  const currentQuoteContext: QuoteRequestContext =
    activeQuoteRef.current.customerQuoteId === customerQuoteId
      ? activeQuoteRef.current
      : {
          customerQuoteId,
          generation: activeQuoteRef.current.generation + 1,
        };

  useLayoutEffect(() => {
    activeQuoteRef.current = currentQuoteContext;
  }, [currentQuoteContext]);

  useLayoutEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useLayoutEffect(() => {
    draftInputRef.current = { versionName, remark, includedItemsText, excludedItemsText };
  }, [versionName, remark, includedItemsText, excludedItemsText]);

  const captureDraftCheckpoint = useCallback((): DraftCheckpoint => ({
    ...draftInputRef.current, editRevision: editRevisionRef.current,
  }), []);

  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      latestPreviewRequestRef.current += 1;
      latestStandardAllocationRequestRef.current += 1;
    };
  }, []);

  useEffect(() => {
    handledQueryEditorRef.current = null;
    setEditorBaseline(null);
    snapshotRef.current = null;
    setSnapshot(null);
    setVersionName("");
    setIncludedItemsText("");
    setExcludedItemsText("");
    setSuccessfulPreview(null);
    successfulPreviewRef.current = null;
    setRemark("");
    setIsDirty(false);
    setEditError(null);
    setReloadError(null);
    setWriteConflict(null);
    setLoadingLatestAfterConflict(false);
    setConflictReloadError(null);
    setPreviewError(null);
    setStandardAllocationState(null);
    setStandardAllocationErrorState(null);
    latestStandardAllocationRequestRef.current += 1;
  }, [customerQuoteId]);

  useEffect(() => {
    if (!queriedEditor || queriedEditor === handledQueryEditorRef.current) return;
    const baseline = editorRef.current;
    if (writeConflict && baseline) return;
    if (isDirty && baseline) {
      if (queriedEditor.quoteRevision !== baseline.quoteRevision) {
        setWriteConflict(new Error("报价已被其他用户更新，请加载最新版本后继续"));
      } else {
        // 来源发布独立于销售草稿；更新来源修订和差异，保留报价修订与本地输入。
        setEditorBaseline({
          ...baseline,
          sourceRevision: queriedEditor.sourceRevision,
          sourceState: queriedEditor.sourceState,
          sourceChanges: queriedEditor.sourceChanges,
          sourceBlockReason: queriedEditor.sourceBlockReason,
          sourceBudgetChanged: queriedEditor.sourceBudgetChanged,
          readonly: queriedEditor.readonly,
          canEditQuote: queriedEditor.canEditQuote,
          canSave: queriedEditor.canSave,
          canCopy: queriedEditor.canCopy,
          canFinalize: queriedEditor.canFinalize,
          canConfirmSourceChange: queriedEditor.canConfirmSourceChange,
          canGenerateAutoTarget: queriedEditor.canGenerateAutoTarget,
        });
      }
      return;
    }
    handledQueryEditorRef.current = queriedEditor;
    setEditorBaseline(queriedEditor);
    snapshotRef.current = queriedEditor.snapshot;
    setSnapshot(queriedEditor.snapshot);
    setVersionName(queriedEditor.versionName);
    setIncludedItemsText(queriedEditor.snapshot.includedItems.join("\n"));
    setExcludedItemsText(queriedEditor.snapshot.excludedItems.join("\n"));
    setRemark(queriedEditor.remark);
    setEditError(null);
  }, [queriedEditor, isDirty, writeConflict]);

  useEffect(() => {
    if (editor && query.dataUpdatedAt > 0 && !query.error) setReloadError(null);
  }, [editor, query.dataUpdatedAt, query.error]);

  useEffect(() => {
    if (!editor || !snapshot) return;
    const nextDirty = !draftMatchesEditor(editor, snapshot, versionName, remark);
    setIsDirty((current) => (current === nextDirty ? current : nextDirty));
  }, [editor, remark, snapshot, versionName]);

  const isCurrentWriteRequest = useCallback((request: WriteRequestContext) => (
    mountedRef.current && belongsToQuoteContext(request, activeQuoteRef.current)
  ), []);

  const invalidateStandardAllocation = useCallback(() => {
    latestStandardAllocationRequestRef.current += 1;
    setStandardAllocationState(null);
    setStandardAllocationErrorState(null);
  }, []);

  const applyEditorData = useCallback(async (
    data: CustomerQuoteEditorData,
    request: WriteRequestContext,
    checkpoint?: DraftCheckpoint,
  ) => {
    if (!isCurrentWriteRequest(request)) return;
    // 写成功后的权威响应优先于此前发起的读取，防止旧 GET 晚到使基线倒退。
    await queryClient.cancelQueries({ queryKey: queryKey(request.customerQuoteId), exact: true });
    if (!isCurrentWriteRequest(request)) return;
    invalidateStandardAllocation();
    latestPreviewRequestRef.current += 1;
    let nextSnapshot = data.snapshot;
    if (checkpoint) {
      for (const edit of confirmationEditsRef.current) {
        if (edit.key === writeLockKey(request) && edit.revision > checkpoint.editRevision) {
          nextSnapshot = edit.apply(nextSnapshot);
        }
      }
      confirmationEditsRef.current = confirmationEditsRef.current.filter((edit) => edit.key !== writeLockKey(request) || edit.revision > checkpoint.editRevision);
    }
    const localInput = draftInputRef.current;
    const nextInput = {
      versionName: checkpoint && localInput.versionName !== checkpoint.versionName ? localInput.versionName : data.versionName,
      remark: checkpoint && localInput.remark !== checkpoint.remark ? localInput.remark : data.remark,
      includedItemsText: checkpoint && localInput.includedItemsText !== checkpoint.includedItemsText ? localInput.includedItemsText : nextSnapshot.includedItems.join("\n"),
      excludedItemsText: checkpoint && localInput.excludedItemsText !== checkpoint.excludedItemsText ? localInput.excludedItemsText : nextSnapshot.excludedItems.join("\n"),
    };
    handledQueryEditorRef.current = data;
    editorRef.current = data;
    draftInputRef.current = nextInput;
    queryClient.setQueryData(queryKey(request.customerQuoteId), data);
    setEditorBaseline(data);
    snapshotRef.current = nextSnapshot;
    setSnapshot(nextSnapshot);
    setVersionName(nextInput.versionName);
    setIncludedItemsText(nextInput.includedItemsText);
    setExcludedItemsText(nextInput.excludedItemsText);
    setRemark(nextInput.remark);
    setIsDirty(!draftMatchesEditor(data, nextSnapshot, nextInput.versionName, nextInput.remark));
    setWriteConflict(null);
    setConflictReloadError(null);
  }, [invalidateStandardAllocation, isCurrentWriteRequest, queryClient]);

  const enterWriteConflict = useCallback((
    error: unknown,
    request: WriteRequestContext,
  ) => {
    if (!isHttpClientError(error) || error.statusCode !== 409) return;
    if (!isCurrentWriteRequest(request)) return;
    if (error.code === "CUSTOMER_QUOTE_SOURCE_CHANGE_STALE" || error.code === "CUSTOMER_QUOTE_SOURCE_CHANGED") {
      void queryClient.invalidateQueries({ queryKey: queryKey(request.customerQuoteId), exact: true });
      return;
    }
    setWriteConflict(error);
    setConflictReloadError(null);
  }, [isCurrentWriteRequest, queryClient]);

  const transformSnapshot = useCallback((transform: (value: CustomerQuoteSnapshot) => CustomerQuoteSnapshot) => {
    const current = snapshotRef.current;
    if (!current) return;
    try {
      const apply = (value: CustomerQuoteSnapshot) => recalculateCustomerQuoteTax(roundCustomerQuoteAutoServiceFee(transform(value)));
      const next = apply(current);
      const key = writeLockKey(activeQuoteRef.current);
      const revision = ++editRevisionRef.current;
      if (confirmationQueuesRef.current.has(key)) confirmationEditsRef.current.push({ key, revision, apply });
      snapshotRef.current = next;
      setSnapshot(next);
      setIsDirty(true);
      setEditError(null);
    } catch (error) {
      setEditError(error instanceof Error ? error : new Error("报价编辑失败，请稍后重试。"));
    }
  }, []);

  const saveMutation = useMutation({
    mutationFn: async ({
      request,
      requestEditor,
      requestSnapshot,
      requestVersionName,
      requestRemark,
    }: {
      request: WriteRequestContext;
      requestEditor: CustomerQuoteEditorData | null;
      requestSnapshot: CustomerQuoteSnapshot | null;
      requestVersionName: string;
      requestRemark: string;
      checkpoint?: DraftCheckpoint;
    }) => {
      if (!requestEditor || !requestSnapshot) throw new Error("报价编辑器尚未就绪");
      if (!requestVersionName.trim()) throw new Error("请填写报价版本名称，不能只输入空格。");
      if (!requestEditor.canSave) {
        throw new Error(requestEditor.sourceBlockReason || "当前报价不能保存");
      }
      return saveCustomerQuoteDraft(request.customerQuoteId, {
        versionName: requestVersionName,
        remark: requestRemark,
        snapshot: requestSnapshot,
        expectedQuoteRevision: requestEditor.quoteRevision,
        expectedSourceRevision: requestEditor.sourceRevision,
      });
    },
    onSuccess: (data, variables) => applyEditorData(data, variables.request, variables.checkpoint),
    onError: (error, variables) => enterWriteConflict(error, variables.request),
  });

  const previewMutation = useMutation({
    mutationFn: async ({
      request,
      templateType,
      snapshot: previewSnapshot,
    }: {
      request: PreviewRequestContext;
      templateType?: CustomerQuoteTemplateType;
      snapshot: CustomerQuoteSnapshot;
    }) => {
      const preview = await previewCustomerQuote(
        request.customerQuoteId,
        templateType ? applyCustomerQuoteTemplate(previewSnapshot, templateType) : previewSnapshot,
      );
      return { request, preview };
    },
    onSuccess: ({ request, preview }, variables) => {
      if (
        mountedRef.current
        && request.requestId === latestPreviewRequestRef.current
        && belongsToQuoteContext(request, activeQuoteRef.current)
      ) {
        setPreviewData(preview);
        setPreviewError(null);
        const successful = {
          ...request,
          snapshotFingerprint: previewFingerprint(variables.snapshot, variables.templateType),
          templateType: preview.templateType,
        };
        successfulPreviewRef.current = successful;
        setSuccessfulPreview(successful);
      }
    },
    onError: (error, variables) => {
      if (
        mountedRef.current
        && variables.request.requestId === latestPreviewRequestRef.current
        && belongsToQuoteContext(variables.request, activeQuoteRef.current)
      ) {
        setPreviewError(error instanceof Error ? error : new Error("客户预览刷新失败，请稍后重试。"));
      }
    },
  });
  const mutatePreview = previewMutation.mutateAsync;
  const requestPreview = useCallback(async (
    templateType?: CustomerQuoteTemplateType,
    previewSnapshot?: CustomerQuoteSnapshot,
  ) => {
    if (!mountedRef.current) throw new Error(supersededPreviewMessage);
    const snapshotToPreview = previewSnapshot ?? snapshot;
    if (!snapshotToPreview) throw new Error("报价编辑器尚未就绪");
    const requestId = latestPreviewRequestRef.current + 1;
    latestPreviewRequestRef.current = requestId;
    const request = { ...activeQuoteRef.current, requestId };
    setPreviewError(null);
    const result = await mutatePreview({ request, templateType, snapshot: snapshotToPreview });
    if (
      !mountedRef.current
      || requestId !== latestPreviewRequestRef.current
      || !belongsToQuoteContext(request, activeQuoteRef.current)
    ) {
      throw new Error(supersededPreviewMessage);
    }
    return result.preview;
  }, [mutatePreview, snapshot]);
  const invalidatePreview = useCallback(() => {
    latestPreviewRequestRef.current += 1;
  }, []);
  const resetPreviewMutation = previewMutation.reset;

  const previewIsCurrent = Boolean(
    snapshot
    && successfulPreview
    && belongsToQuoteContext(successfulPreview, currentQuoteContext)
    && successfulPreview.snapshotFingerprint === previewFingerprint(snapshot, successfulPreview.templateType),
  );

  /** 输出前及响应后复核成功正文、当前草稿和报价代次，避免生成未核对的新金额。 */
  const assertOutputPreview = (
    request: WriteRequestContext,
    requestSnapshot: CustomerQuoteSnapshot,
    templateType = requestSnapshot.templateType,
  ) => {
    if (!isCurrentWriteRequest(request)) throw new Error(supersededWriteMessage);
    const successful = successfulPreviewRef.current;
    const currentSnapshot = snapshotRef.current;
    const fingerprint = previewFingerprint(requestSnapshot, templateType);
    if (
      !successful
      || !belongsToQuoteContext(successful, request)
      || !currentSnapshot
      || successful.snapshotFingerprint !== fingerprint
      || previewFingerprint(currentSnapshot, templateType) !== fingerprint
    ) {
      throw new Error("当前草稿尚未成功同步客户预览，请先刷新客户预览再复制或下载。");
    }
  };

  useEffect(() => {
    resetPreviewMutation();
    setPreviewData(null);
    latestPreviewRequestRef.current += 1;
  }, [customerQuoteId, resetPreviewMutation]);

  const standardAllocationMutation = useMutation({
    mutationFn: async ({
      requestId,
      snapshotFingerprint,
      requestSnapshot,
      persistence,
    }: {
      requestId: number;
      snapshotFingerprint: string;
      requestSnapshot: CustomerQuoteSnapshot;
      persistence?: { customerQuoteId: string; expectedQuoteRevision: string };
    }) => ({
      requestId,
      snapshotFingerprint,
      requestSnapshot,
      data: persistence
        ? await calculateCustomerQuoteAllocation(requestSnapshot, persistence)
        : await calculateCustomerQuoteAllocation(requestSnapshot),
    }),
  });
  const mutateStandardAllocation = standardAllocationMutation.mutateAsync;

  const snapshotFingerprint = useMemo(
    () => (
      snapshot
        ? stableJson(canonicalSnapshot(recalculateCustomerQuoteTax(snapshot)))
        : null
    ),
    [snapshot],
  );
  const visibleStandardAllocationState = standardAllocationState?.snapshotFingerprint
    === snapshotFingerprint
    ? standardAllocationState
    : null;
  const visibleStandardAllocationErrorState = standardAllocationErrorState?.snapshotFingerprint
    === snapshotFingerprint
    ? standardAllocationErrorState
    : null;

  useEffect(() => {
    invalidateStandardAllocation();
  }, [invalidateStandardAllocation, snapshotFingerprint]);

  const calculateStandardAllocation = useCallback(async () => {
    if (!mountedRef.current) throw new Error(supersededAllocationMessage);
    const current = snapshotRef.current;
    if (!current) throw new Error("报价编辑器尚未就绪");
    const requestSnapshot = recalculateCustomerQuoteTax(current);
    const requestFingerprint = stableJson(canonicalSnapshot(requestSnapshot));
    const requestId = latestStandardAllocationRequestRef.current + 1;
    latestStandardAllocationRequestRef.current = requestId;
    const requestContext = { ...activeQuoteRef.current };
    const currentEditor = editorRef.current;
    const persistence = currentEditor
      && currentEditor.status === "draft"
      && draftMatchesEditor(currentEditor, current, versionName, remark)
      ? {
          customerQuoteId: currentEditor.customerQuoteId,
          expectedQuoteRevision: currentEditor.quoteRevision,
        }
      : undefined;
    setStandardAllocationState(null);
    setStandardAllocationErrorState(null);
    try {
      const result = await mutateStandardAllocation({
        requestId,
        snapshotFingerprint: requestFingerprint,
        requestSnapshot,
        persistence,
      });
      const currentSnapshot = snapshotRef.current;
      const currentFingerprint = currentSnapshot
        ? stableJson(canonicalSnapshot(recalculateCustomerQuoteTax(currentSnapshot)))
        : null;
      const isCurrentRequest = mountedRef.current
        && requestId === latestStandardAllocationRequestRef.current
        && requestContext.customerQuoteId === activeQuoteRef.current.customerQuoteId
        && requestContext.generation === activeQuoteRef.current.generation
        && requestFingerprint === currentFingerprint;
      if (!isCurrentRequest) throw new Error(supersededAllocationMessage);
      setStandardAllocationState({
        snapshotFingerprint: requestFingerprint,
        data: result.data,
        financialSummary: calculateCustomerQuoteStandardFinancialSummary(
          requestSnapshot,
          result.data,
        ),
      });
      return result.data;
    } catch (error) {
      const currentSnapshot = snapshotRef.current;
      const currentFingerprint = currentSnapshot
        ? stableJson(canonicalSnapshot(recalculateCustomerQuoteTax(currentSnapshot)))
        : null;
      const isCurrentRequest = mountedRef.current
        && requestId === latestStandardAllocationRequestRef.current
        && requestContext.customerQuoteId === activeQuoteRef.current.customerQuoteId
        && requestContext.generation === activeQuoteRef.current.generation
        && requestFingerprint === currentFingerprint;
      if (isCurrentRequest) {
        setStandardAllocationErrorState({
          snapshotFingerprint: requestFingerprint,
          error: error instanceof Error
            ? error
            : new Error("标准报价试算失败，请稍后重试。"),
        });
      }
      throw error;
    }
  }, [mutateStandardAllocation, remark, versionName]);

  const confirmSourceChangeMutation = useMutation({
    mutationFn: async ({ request, requestEditor, requestIsDirty, requestSnapshot, requestVersionName, requestRemark, changeId, baseMarkup, financeTreatment }: {
      request: WriteRequestContext;
      requestEditor: CustomerQuoteEditorData | null;
      requestIsDirty: boolean;
      changeId: string;
      requestSnapshot: CustomerQuoteSnapshot | null;
      requestVersionName: string;
      requestRemark: string;
      baseMarkup?: string;
      financeTreatment?: CustomerQuoteLine["financeTreatment"];
    }) => {
      if (!requestEditor?.canConfirmSourceChange) {
        throw new Error(requestEditor?.sourceBlockReason || "当前核价变化不能确认");
      }
      const checkpoint = captureDraftCheckpoint();
      const savedEditor = requestIsDirty
        ? await saveMutation.mutateAsync({ request, requestEditor, requestSnapshot, requestVersionName, requestRemark, checkpoint })
        : requestEditor;
      if (!isCurrentWriteRequest(request)) throw new Error(supersededWriteMessage);
      const data = await confirmCustomerQuoteSourceChange(
        request.customerQuoteId,
        changeId,
        { ...sourceWriteCommand(savedEditor), ...(baseMarkup === undefined ? {} : { baseMarkup }), ...(financeTreatment === undefined ? {} : { financeTreatment }) },
      );
      return { data, checkpoint };
    },
    onSuccess: ({ data, checkpoint }, variables) => applyEditorData(data, variables.request, checkpoint),
    onError: (error, variables) => enterWriteConflict(error, variables.request),
  });

  const autoTargetMutation = useMutation({
    mutationFn: ({ request, requestEditor, requestIsDirty, discardLocalChanges }: {
      request: WriteRequestContext;
      requestEditor: CustomerQuoteEditorData | null;
      requestIsDirty: boolean;
      discardLocalChanges: boolean;
    }) => {
      if (!requestEditor?.canGenerateAutoTarget) {
        throw new Error(requestEditor?.sourceBlockReason || "当前报价不能生成 16% 报价");
      }
      if (requestIsDirty && !discardLocalChanges) {
        throw new Error("未保存修改将丢失，请确认后继续");
      }
      return generateCustomerQuoteAutoTarget(
        request.customerQuoteId,
        sourceWriteCommand(requestEditor),
      );
    },
    onSuccess: (data, variables) => applyEditorData(data, variables.request),
    onError: (error, variables) => enterWriteConflict(error, variables.request),
  });

  const downloadMutation = useMutation({
    mutationFn: async ({
      request,
      requestEditor,
      requestSnapshot,
      requestVersionName,
      templateType,
    }: {
      request: WriteRequestContext;
      requestEditor: CustomerQuoteEditorData | null;
      requestSnapshot: CustomerQuoteSnapshot | null;
      requestVersionName: string;
      templateType?: CustomerQuoteTemplateType;
    }) => {
      if (!requestEditor || !requestSnapshot) throw new Error("报价编辑器尚未就绪");
      if (!requestVersionName.trim()) throw new Error("请填写报价版本名称，不能只输入空格。");
      assertOutputPreview(request, requestSnapshot, templateType);
      const exportSnapshot = templateType
        ? applyCustomerQuoteTemplate(requestSnapshot, templateType)
        : requestSnapshot;
      const download = await fetchCustomerQuoteExcelDownload(
        request.customerQuoteId,
        requestVersionName,
        exportSnapshot,
      );
      assertOutputPreview(request, requestSnapshot, templateType);
      triggerCustomerQuoteExcelDownload(download);
    },
  });

  const markdownMutation = useMutation({
    mutationFn: async ({
      request,
      requestSnapshot,
      requestVersionName,
      templateType,
    }: {
      request: WriteRequestContext;
      requestSnapshot: CustomerQuoteSnapshot | null;
      requestVersionName: string;
      templateType?: CustomerQuoteTemplateType;
    }) => {
      if (!requestSnapshot) throw new Error("报价编辑器尚未就绪");
      if (!requestVersionName.trim()) throw new Error("请填写报价版本名称，不能只输入空格。");
      assertOutputPreview(request, requestSnapshot, templateType);
      const exportSnapshot = templateType
        ? applyCustomerQuoteTemplate(requestSnapshot, templateType)
        : requestSnapshot;
      const markdown = await exportCustomerQuoteMarkdown(request.customerQuoteId, exportSnapshot);
      assertOutputPreview(request, requestSnapshot, templateType);
      return markdown;
    },
  });

  const finalizeMutation = useMutation({
    mutationFn: async ({ request, requestEditor, requestIsDirty }: {
      request: WriteRequestContext;
      requestEditor: CustomerQuoteEditorData | null;
      requestIsDirty: boolean;
    }) => {
      if (requestIsDirty) throw new Error("请先保存当前草稿再定稿");
      if (!requestEditor?.versionName.trim()) throw new Error("请填写报价版本名称，不能只输入空格。");
      if (!requestEditor?.canFinalize) {
        throw new Error(requestEditor?.sourceBlockReason || "当前报价不能定稿");
      }
      return finalizeCustomerQuote(
        request.customerQuoteId,
        sourceWriteCommand(requestEditor),
      );
    },
    onSuccess: (data, variables) => applyEditorData(data, variables.request),
    onError: (error, variables) => enterWriteConflict(error, variables.request),
  });

  const repriceTargetMutation = useMutation({
    mutationFn: async (variables: {
      request: WriteRequestContext;
      requestEditor: CustomerQuoteEditorData;
      requestSnapshot: CustomerQuoteSnapshot;
      kind: "per_student_difference" | "margin_rate";
      amount: string;
    }) => repriceCustomerQuoteTarget(
      variables.request.customerQuoteId, variables.requestSnapshot,
      variables.kind, variables.amount, { expectedQuoteRevision: variables.requestEditor.quoteRevision, expectedSourceRevision: variables.requestEditor.sourceRevision },
    ),
    onSuccess: (data, variables) => {
      if (!isCurrentWriteRequest(variables.request)) throw new Error(supersededWriteMessage);
      if (snapshotRef.current !== variables.requestSnapshot) throw new Error("草稿已修改，已忽略旧目标计算结果，请重新计算");
      snapshotRef.current = data;
      setSnapshot(data);
      setIsDirty(true);
      setEditError(null);
    },
    onError: (error, variables) => enterWriteConflict(error, variables.request),
  });

  /** 明确放弃冲突草稿后重新读取服务端最新版本。 */
  const loadLatestAfterConflict = useCallback(async () => {
    if (!writeConflict) return;
    const request: WriteRequestContext = {
      ...currentQuoteContext,
      requestId: writeRequestSequenceRef.current + 1,
    };
    writeRequestSequenceRef.current = request.requestId;
    setLoadingLatestAfterConflict(true);
    setConflictReloadError(null);
    try {
      const result = await query.refetch();
      if (result.error) throw result.error;
      if (!result.data || result.data.customerQuoteId !== request.customerQuoteId) {
        throw new Error("最新报价版本暂不可用，请稍后重试");
      }
      if (!isCurrentWriteRequest(request)) throw new Error(supersededWriteMessage);
      await applyEditorData(result.data, request);
      saveMutation.reset();
      confirmSourceChangeMutation.reset();
      autoTargetMutation.reset();
      finalizeMutation.reset();
    } catch (error) {
      if (isCurrentWriteRequest(request)) {
        setConflictReloadError(
          error instanceof Error ? error : new Error("最新报价版本加载失败，请稍后重试"),
        );
      }
      throw error;
    } finally {
      if (isCurrentWriteRequest(request)) setLoadingLatestAfterConflict(false);
    }
  }, [
    applyEditorData,
    currentQuoteContext,
    isCurrentWriteRequest,
    query,
    autoTargetMutation,
    confirmSourceChangeMutation,
    finalizeMutation,
    saveMutation,
    writeConflict,
  ]);
  const saveIsCurrent = belongsToQuoteContext(
    saveMutation.variables?.request,
    currentQuoteContext,
  );
  const confirmIsCurrent = belongsToQuoteContext(
    confirmSourceChangeMutation.variables?.request,
    currentQuoteContext,
  );
  const autoTargetIsCurrent = belongsToQuoteContext(
    autoTargetMutation.variables?.request,
    currentQuoteContext,
  );
  const finalizeIsCurrent = belongsToQuoteContext(
    finalizeMutation.variables?.request,
    currentQuoteContext,
  );
  const downloadIsCurrent = belongsToQuoteContext(
    downloadMutation.variables?.request,
    currentQuoteContext,
  );
  const markdownIsCurrent = belongsToQuoteContext(
    markdownMutation.variables?.request,
    currentQuoteContext,
  );
  const saving = saveIsCurrent && saveMutation.isPending && saveMutation.variables?.request.kind !== "source-confirmation";
  const confirmingSourceChange = belongsToQuoteContext(confirmationActivity?.context, currentQuoteContext)
    && (confirmationActivity?.count ?? 0) > 0;
  const generatingAutoTarget = autoTargetIsCurrent && autoTargetMutation.isPending;
  const finalizing = finalizeIsCurrent && finalizeMutation.isPending;
  const downloading = downloadIsCurrent && downloadMutation.isPending;
  const exportingMarkdown = markdownIsCurrent && markdownMutation.isPending;
  const nonWriteActionBusy = previewMutation.isPending
    || downloading
    || exportingMarkdown;

  const runWriteMutation = useCallback(async <T,>(
    operation: (request: WriteRequestContext) => Promise<T>,
  ) => {
    if (writeConflict) {
      throw new Error("报价存在并发冲突，请先加载最新版本");
    }
    const lockKey = writeLockKey(currentQuoteContext);
    if (nonWriteActionBusy || activeWriteRequestsRef.current.has(lockKey)) {
      throw new Error("操作进行中，请稍后再试");
    }
    const request: WriteRequestContext = {
      ...currentQuoteContext,
      requestId: writeRequestSequenceRef.current + 1,
    };
    writeRequestSequenceRef.current = request.requestId;
    activeWriteRequestsRef.current.set(lockKey, request);
    try {
      const result = await operation(request);
      if (!isCurrentWriteRequest(request)) throw new Error(supersededWriteMessage);
      return result;
    } finally {
      if (activeWriteRequestsRef.current.get(lockKey) === request) {
        activeWriteRequestsRef.current.delete(lockKey);
      }
    }
  }, [
    currentQuoteContext,
    isCurrentWriteRequest,
    nonWriteActionBusy,
    writeConflict,
  ]);

  /** 连续确认按最新修订自动衔接；仅当前项目防重，等待期间继续保留销售编辑。 */
  async function enqueueSourceConfirmation(changeId: string, baseMarkup?: string, financeTreatment?: CustomerQuoteLine["financeTreatment"]): Promise<CustomerQuoteEditorData> {
    if (writeConflict) throw new Error("报价存在并发冲突，请先加载最新版本");
    const key = writeLockKey(currentQuoteContext);
    let queue = confirmationQueuesRef.current.get(key);
    if (!queue) {
      if (nonWriteActionBusy || activeWriteRequestsRef.current.has(key)) throw new Error("操作进行中，请稍后再试");
      const request: WriteRequestContext = { ...currentQuoteContext, requestId: ++writeRequestSequenceRef.current, kind: "source-confirmation" };
      queue = { request, tail: Promise.resolve(), pending: new Map(), blockedError: null };
      confirmationQueuesRef.current.set(key, queue);
      activeWriteRequestsRef.current.set(key, request);
    }
    const existing = queue.pending.get(changeId);
    if (existing) return existing;
    const activeQueue = queue;
    const work = activeQueue.tail.then(async () => {
      if (!isCurrentWriteRequest(activeQueue.request)) throw new Error(supersededWriteMessage);
      if (activeQueue.blockedError) throw activeQueue.blockedError;
      const requestEditor = editorRef.current;
      const requestSnapshot = snapshotRef.current;
      const input = draftInputRef.current;
      if (!requestEditor || !requestSnapshot) throw new Error("报价编辑器尚未就绪");
      try {
        const result = await confirmSourceChangeMutation.mutateAsync({
          request: activeQueue.request, requestEditor, requestSnapshot,
          requestIsDirty: !draftMatchesEditor(requestEditor, requestSnapshot, input.versionName, input.remark),
          requestVersionName: input.versionName, requestRemark: input.remark,
          changeId, baseMarkup, financeTreatment,
        });
        if (!isCurrentWriteRequest(activeQueue.request)) throw new Error(supersededWriteMessage);
        return result.data;
      } catch (error) {
        if (isHttpClientError(error) && error.statusCode === 409) activeQueue.blockedError = error;
        throw error;
      }
    });
    activeQueue.pending.set(changeId, work);
    activeQueue.tail = work.then(() => undefined, () => undefined);
    setConfirmationActivity({ context: activeQueue.request, count: activeQueue.pending.size });
    try { return await work; }
    finally {
      activeQueue.pending.delete(changeId);
      if (isCurrentWriteRequest(activeQueue.request)) setConfirmationActivity({ context: activeQueue.request, count: activeQueue.pending.size });
      if (!activeQueue.pending.size) {
        confirmationQueuesRef.current.delete(key);
        confirmationEditsRef.current = confirmationEditsRef.current.filter((edit) => edit.key !== key);
        if (activeWriteRequestsRef.current.get(key) === activeQueue.request) activeWriteRequestsRef.current.delete(key);
      }
    }
  }

  const summary = useMemo(
    () => (snapshot ? calculateCustomerQuoteSummary(snapshot) : null),
    [snapshot],
  );
  const roleSummary = useMemo(
    () => (snapshot ? calculateCustomerQuoteRoleSummary(snapshot) : null),
    [snapshot],
  );

  // 来源确认使用最近已发布来源；脏草稿先保存，自动重算仍沿用显式放弃保护。
  const sourceRebuildEditor = queriedEditor?.quoteRevision === editor?.quoteRevision
    ? queriedEditor
    : editor;

  /** 重新读取编辑器；失败时保留已有数据并暴露非阻塞恢复错误。 */
  const reload = useCallback(async () => {
    const request: WriteRequestContext = {
      ...currentQuoteContext,
      requestId: writeRequestSequenceRef.current + 1,
    };
    writeRequestSequenceRef.current = request.requestId;
    latestReloadRequestRef.current = request;
    const result = await query.refetch();
    if (!isCurrentWriteRequest(request) || latestReloadRequestRef.current !== request) {
      throw new Error(supersededWriteMessage);
    }
    if (result.error) {
      const error = result.error instanceof Error
        ? result.error
        : new Error("重新检查失败，请稍后重试");
      setReloadError(error);
      throw error;
    }
  }, [currentQuoteContext, isCurrentWriteRequest, query]);

  /** 只恢复本次已加载的保存基线；并发冲突必须走加载最新版本。 */
  const restoreSavedDraft = async () => {
    if (!editor || editor.readonly) throw new Error("当前报价不能恢复草稿");
    if (writeConflict) throw new Error("报价存在并发冲突，请先加载最新版本");
    if (activeWriteRequestsRef.current.has(writeLockKey(currentQuoteContext)) || downloading || exportingMarkdown) {
      throw new Error("操作进行中，请稍后再试");
    }
    const request = { ...currentQuoteContext, requestId: ++writeRequestSequenceRef.current };
    await applyEditorData(editor, request);
    if (!isCurrentWriteRequest(request)) return;
    setPreviewData(null);
    setPreviewError(null);
    successfulPreviewRef.current = null;
    setSuccessfulPreview(null);
    setEditError(null);
    previewMutation.reset();
    saveMutation.reset();
    confirmSourceChangeMutation.reset();
    autoTargetMutation.reset();
    finalizeMutation.reset();
  };

  return {
    editor,
    repriceTarget: async (kind: "per_student_difference" | "margin_rate", amount: string) => {
      const current = snapshotRef.current;
      if (!current || !editor || editor.readonly || !editor.canEditQuote) throw new Error("当前报价不能重新计算");
      return runWriteMutation((request) => repriceTargetMutation.mutateAsync({
        request, requestEditor: editor, requestSnapshot: current, kind, amount,
      }));
    },
    repricingTarget: belongsToQuoteContext(repriceTargetMutation.variables?.request, currentQuoteContext)
      && repriceTargetMutation.isPending,
    snapshot,
    versionName,
    versionNameError,
    includedItemsText,
    excludedItemsText,
    remark,
    isLoading: query.isPending,
    isOffline: query.fetchStatus === "paused",
    error: editor ? null : query.error,
    reloading: query.isFetching,
    reload,
    restoreSavedDraft,
    reloadError,
    writeConflict,
    loadingLatestAfterConflict,
    conflictReloadError,
    loadLatestAfterConflict,
    isDirty,
    summary,
    roleSummary,
    readonly: editor?.readonly ?? false,
    sourceBudgetChanged: editor?.sourceBudgetChanged ?? false,
    editError,
    saving,
    saveError: saveIsCurrent ? saveMutation.error : null,
    previewing: previewMutation.isPending,
    previewData,
    previewError,
    previewIsCurrent,
    calculatingStandardAllocation: standardAllocationMutation.isPending
      && standardAllocationMutation.variables?.requestId
        === latestStandardAllocationRequestRef.current
      && standardAllocationMutation.variables?.snapshotFingerprint === snapshotFingerprint,
    standardAllocation: visibleStandardAllocationState?.data ?? null,
    standardFinancialSummary: visibleStandardAllocationState?.financialSummary ?? null,
    standardAllocationError: visibleStandardAllocationErrorState?.error ?? null,
    confirmingSourceChange,
    confirmSourceChangeError: confirmIsCurrent ? confirmSourceChangeMutation.error : null,
    generatingAutoTarget,
    generateAutoTargetError: autoTargetIsCurrent ? autoTargetMutation.error : null,
    downloading,
    downloadError: downloadIsCurrent ? downloadMutation.error : null,
    exportingMarkdown,
    exportMarkdownError: markdownIsCurrent ? markdownMutation.error : null,
    finalizing,
    finalizeError: finalizeIsCurrent ? finalizeMutation.error : null,
    setVersionName: (value: string) => {
      draftInputRef.current.versionName = value;
      setVersionName(value);
      setIsDirty(true);
    },
    setRemark: (value: string) => {
      draftInputRef.current.remark = value;
      setRemark(value);
      setIsDirty(true);
    },
    setStudentCount: (studentCount: number) =>
      transformSnapshot((current) =>
        applyCustomerQuotePeopleCounts(current, {
          studentCount,
          teacherCount: current.teacherCount,
        }),
      ),
    setTeacherCount: (teacherCount: number) =>
      transformSnapshot((current) =>
        applyCustomerQuotePeopleCounts(current, {
          studentCount: current.studentCount,
          teacherCount,
        }),
      ),
    setStudentUnitPrice: (amount: string) =>
      transformSnapshot((current) => applyCustomerQuoteRoleUnitPrice(current, "student", amount)),
    setTeacherUnitPrice: (amount: string) =>
      transformSnapshot((current) => applyCustomerQuoteRoleUnitPrice(current, "teacher", amount)),
    setTemplate: (template: CustomerQuoteTemplateType) =>
      transformSnapshot((current) => applyCustomerQuoteTemplate(current, template)),
    setIncludedItems: (items: string[]) => {
      draftInputRef.current.includedItemsText = items.join("\n");
      setIncludedItemsText(items.join("\n"));
      transformSnapshot((current) => ({ ...current, includedItems: items }));
    },
    setExcludedItems: (items: string[]) => {
      draftInputRef.current.excludedItemsText = items.join("\n");
      setExcludedItemsText(items.join("\n"));
      transformSnapshot((current) => ({ ...current, excludedItems: items }));
    },
    setIncludedItemsText: (value: string) => {
      draftInputRef.current.includedItemsText = value;
      setIncludedItemsText(value);
      transformSnapshot((current) => ({ ...current, includedItems: splitItems(value) }));
    },
    setExcludedItemsText: (value: string) => {
      draftInputRef.current.excludedItemsText = value;
      setExcludedItemsText(value);
      transformSnapshot((current) => ({ ...current, excludedItems: splitItems(value) }));
    },
    normalizeIncludedItemsText: () => setIncludedItemsText((value) => splitItems(value).join("\n")),
    normalizeExcludedItemsText: () => setExcludedItemsText((value) => splitItems(value).join("\n")),
    setStayHighMarkupConfirmed: (confirmed: boolean) =>
      transformSnapshot((current) => ({ ...current, stayHighMarkupConfirmed: confirmed })),
    updateLine: (lineNo: number, patch: Partial<CustomerQuoteLine>) =>
      transformSnapshot((current) => ({
        ...current,
        lines: current.lines.map((line) =>
          line.lineNo === lineNo ? patchCustomerQuoteLine(line, patch) : line,
        ),
      })),
    setLinesAgentCollection: (lineNos: number[], checked: boolean) =>
      transformSnapshot((current) => ({
        ...current,
        lines: current.lines.map((line) =>
          lineNos.includes(line.lineNo)
            ? applyCustomerQuoteAgentCollection(line, checked)
            : line,
        ),
      })),
    setLineUnitMarkup: (lineNo: number, markup: string) =>
      transformSnapshot((current) => {
        const currentLine = current.lines.find((line) => line.lineNo === lineNo);
        const lines = current.lines.map((line) =>
          line.lineNo === lineNo ? applyCustomerQuoteLineUnitMarkup(line, markup) : line,
        );
        const nextLine = lines.find((line) => line.lineNo === lineNo);
        const stayMarkupChanged = currentLine !== undefined
          && isCustomerQuotePersonNightStay(current, currentLine)
          && currentLine.quoteUnitPrice !== nextLine?.quoteUnitPrice;
        return {
          ...current,
          lines,
          stayHighMarkupConfirmed: stayMarkupChanged && current.stayHighMarkupConfirmed
            ? false
            : current.stayHighMarkupConfirmed,
        };
      }),
    setPricingGroupMarkup: (lineNos: number[], markup: string) =>
      transformSnapshot((current) =>
        applyCustomerQuotePricingGroupMarkup(current, lineNos, markup),
      ),
    setGroupDisplayMode: (
      lineNos: number[],
      displayMode: CustomerQuoteLine["displayMode"],
      templateType?: CustomerQuoteTemplateType,
      previewImmediately = true,
    ) => {
      if (!snapshot) return Promise.reject(new Error("报价编辑器尚未就绪"));
      transformSnapshot((current) => ({
        ...current,
        lines: current.lines.map((line) =>
          lineNos.includes(line.lineNo) ? {
            ...line,
            displayMode,
            mergeTargetGroup: displayMode === "merge"
              ? line.feeCategory === "misc"
                ? "其他"
                : customerQuoteFeeCategoryLabels[line.feeCategory]
              : displayMode === "show"
                ? null
                : line.mergeTargetGroup,
          } : line,
        ),
      }));
      const nextSnapshot = snapshotRef.current!;
      return previewImmediately
        ? requestPreview(templateType, nextSnapshot)
        : Promise.resolve(undefined);
    },
    addManualLine: () =>
      transformSnapshot((current) => ({
        ...current,
        lines: [
          ...current.lines,
          createManualCustomerQuoteLine(Math.max(0, ...current.lines.map((line) => line.lineNo)) + 1),
        ],
      })),
    removeManualLine: (lineNo: number) =>
      transformSnapshot((current) => ({
        ...current,
        lines: current.lines.filter((line) => line.lineNo !== lineNo || line.lineOrigin !== "manual"),
      })),
    save: () => runWriteMutation((request) => saveMutation.mutateAsync({
      request,
      requestEditor: editor,
      requestSnapshot: snapshot,
      requestVersionName: versionName,
      requestRemark: remark,
    })),
    preview: requestPreview,
    invalidatePreview,
    calculateStandardAllocation,
    invalidateStandardAllocation,
    confirmSourceChange: (changeId: string, discardLocalChanges = false, baseMarkup?: string, financeTreatment?: CustomerQuoteLine["financeTreatment"]) => {
      // 兼容旧调用参数；确认来源始终先保存销售输入，不再允许借此放弃草稿。
      void discardLocalChanges;
      return enqueueSourceConfirmation(changeId, baseMarkup, financeTreatment);
    },
    generateAutoTarget: (discardLocalChanges = false) =>
      runWriteMutation((request) => autoTargetMutation.mutateAsync({
        request,
        requestEditor: sourceRebuildEditor,
        requestIsDirty: isDirty,
        discardLocalChanges,
      })),
    downloadExcel: async (templateType?: CustomerQuoteTemplateType) => {
      const request: WriteRequestContext = {
        ...currentQuoteContext,
        requestId: writeRequestSequenceRef.current + 1,
      };
      writeRequestSequenceRef.current = request.requestId;
      await downloadMutation.mutateAsync({
        request,
        requestEditor: editor,
        requestSnapshot: snapshot,
        requestVersionName: versionName,
        templateType,
      });
    },
    exportMarkdown: async (templateType?: CustomerQuoteTemplateType) => {
      const request: WriteRequestContext = {
        ...currentQuoteContext,
        requestId: writeRequestSequenceRef.current + 1,
      };
      writeRequestSequenceRef.current = request.requestId;
      const result = await markdownMutation.mutateAsync({
        request,
        requestSnapshot: snapshot,
        requestVersionName: versionName,
        templateType,
      });
      if (!isCurrentWriteRequest(request)) throw new Error(supersededWriteMessage);
      return result;
    },
    finalize: () => runWriteMutation((request) => finalizeMutation.mutateAsync({
      request,
      requestEditor: editor,
      requestIsDirty: isDirty,
    })),
  };
}
